import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import MainLayout from "@/layouts/MainLayout";

interface ThankYouProps {
  title: string;
  description: string;
  mainContent: React.ReactNode;
  additionalContent?: React.ReactNode;
}

const ThankYou = ({ title, description, mainContent, additionalContent }: ThankYouProps) => {
  return (
    <MainLayout
      title={`${title} | Niumora`}
      description={description}
      ogType="website"
    >
      <div className="min-h-screen bg-slate-50 py-16 md:py-24">
        <div className="container max-w-3xl mx-auto px-4 md:px-6">
          <Card className="p-6 md:p-8 lg:p-10">
            <CardContent className="p-0 space-y-6">
              {/* Success Icon */}
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              {/* Main Content */}
              <div className="text-center space-y-4">
                <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
                {mainContent}
              </div>

              {/* Additional Content */}
              {additionalContent && (
                <div className="border-t border-gray-200 pt-6 mt-6">
                  {additionalContent}
                </div>
              )}

              {/* Social Links */}
              <div className="border-t border-gray-200 pt-6">
                <p className="text-center text-sm text-muted-foreground mb-4">
                  Follow us for more AI tips and updates:
                </p>
                <div className="flex justify-center space-x-6">
                  <a
                    href="https://www.facebook.com/people/Niumora/61574181287893/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <span className="sr-only">Facebook</span>
                    <Facebook className="h-6 w-6" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@niumora_ai?lang=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <span className="sr-only">TikTok</span>
                    <svg
                      className="h-6 w-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.youtube.com/@niumora"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <span className="sr-only">YouTube</span>
                    <Youtube className="h-6 w-6" />
                  </a>
                </div>
              </div>

              {/* Return Home Button */}
              <div className="text-center">
                <Button
                  variant="outline"
                  className="rounded-full"
                  asChild
                >
                  <Link to="/">
                    Return Home <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ThankYou;
